export type GdprRightSlug = "effacement" | "acces" | "rectification" | "limitation" | "portabilite" | "opposition"

export type GdprRequestType = "erasure" | "access" | "rectification" | "restriction" | "portability" | "objection"

export interface GdprRightConfig {
    slug: GdprRightSlug
    type: GdprRequestType
    label: string
    title: string
    article: string
    intro: string
    footer: string
}

export const GDPR_PAGE_PATH = "/mes-droits-rgpd"
export const GDPR_RIGHT_QUERY_PARAM = "droit"
// Only the erasure form is public for now; the five other rights ship later behind this flag
export const GDPR_ALL_RIGHTS_FLAG = "gdpr_all_rights"
export const GDPR_ERASURE_SLUG: GdprRightSlug = "effacement"
export const GDPR_FOOTER_LABEL_ERASURE_ONLY = "Supprimer mes données"
export const GDPR_FOOTER_LABEL_ALL_RIGHTS = "Mes droits RGPD"
export const GDPR_DPO_EMAIL = "dpo@reassurez-moi.fr"
export const GDPR_CONTROLLER_NAME = "Reassurez-moi SAS"
export const GDPR_PRIVACY_POLICY_PATH = "/guide/politique-confidentialite"
export const GDPR_CONFIRMATION_LINK_VALIDITY_HOURS = 48
export const GDPR_ATTACHMENT_MAX_SIZE_BYTES = 5 * 1024 * 1024
export const GDPR_ATTACHMENT_ACCEPT: Record<string, string[]> = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
}

const CONTROLLER_LINE = `Responsable du traitement : ${GDPR_CONTROLLER_NAME} — ${GDPR_DPO_EMAIL}`

export const GDPR_RIGHTS: readonly GdprRightConfig[] = [
    {
        slug: "effacement",
        type: "erasure",
        label: "Droit à l'effacement (suppression de mes données)",
        title: "Demande de suppression de vos données personnelles",
        article: "art. 17 RGPD",
        intro:
            "Conformément au Règlement Général sur la Protection des Données (RGPD), vous pouvez demander la suppression des données personnelles que vous avez confiées à Réassurez-moi. Pour cela, il vous faut indiquer l'adresse email utilisée lors de votre demande de devis : nous vous enverrons un lien pour confirmer votre demande puis celle-ci sera traitée.",
        footer: `Délai de réponse : un mois maximum (art. 17 RGPD). ${CONTROLLER_LINE}`,
    },
    {
        slug: "acces",
        type: "access",
        label: "Droit d'accès (copie de mes données)",
        title: "Demande d'accès à vos données personnelles",
        article: "art. 15 RGPD",
        intro:
            "Conformément à l'article 15 du RGPD, vous pouvez obtenir une copie de l'ensemble des données personnelles que nous détenons à votre sujet. Nous disposons d'un délai maximum de 30 jours pour vous transmettre un export sécurisé.",
        footer: `Délai de traitement : 30 jours maximum (art. 15 RGPD). ${CONTROLLER_LINE}`,
    },
    {
        slug: "rectification",
        type: "rectification",
        label: "Droit de rectification (correction d'une donnée)",
        title: "Demande de rectification de vos données personnelles",
        article: "art. 16 RGPD",
        intro:
            "Conformément à l'article 16 du RGPD, vous pouvez demander la correction d'une donnée personnelle inexacte ou incomplète vous concernant. Nous disposons d'un délai maximum de 30 jours pour traiter votre demande.",
        footer: `Délai de traitement : 30 jours maximum (art. 16 RGPD). ${CONTROLLER_LINE}`,
    },
    {
        slug: "limitation",
        type: "restriction",
        label: "Droit à la limitation (gel du traitement)",
        title: "Demande de limitation du traitement de vos données",
        article: "art. 18 RGPD",
        intro:
            "Conformément à l'article 18 du RGPD, vous pouvez demander le gel temporaire du traitement de vos données, par exemple si vous en contestez l'exactitude ou la licéité. Vos données sont alors conservées en l'état mais ne font plus l'objet d'aucun traitement marketing. Nous disposons d'un délai maximum de 30 jours pour traiter votre demande.",
        footer: `Délai de traitement : 30 jours maximum (art. 18 RGPD). ${CONTROLLER_LINE}`,
    },
    {
        slug: "portabilite",
        type: "portability",
        label: "Droit à la portabilité (export de mes données)",
        title: "Demande de portabilité de vos données personnelles",
        article: "art. 20 RGPD",
        intro:
            "Conformément à l'article 20 du RGPD, vous pouvez récupérer les données personnelles que vous nous avez fournies dans un format structuré et réutilisable (CSV ou JSON). Nous disposons d'un délai maximum de 30 jours pour vous transmettre un export sécurisé.",
        footer: `Délai de traitement : 30 jours maximum (art. 20 RGPD). ${CONTROLLER_LINE}`,
    },
    {
        slug: "opposition",
        type: "objection",
        label: "Droit d'opposition (à un traitement de mes données)",
        title: "Demande d'opposition à un traitement de vos données",
        article: "art. 21 RGPD",
        intro:
            "Conformément à l'article 21 du RGPD, vous pouvez vous opposer à un traitement de vos données personnelles. L'opposition à la prospection commerciale est prise en compte immédiatement et sans justification ; pour tout autre traitement, nous étudions votre demande dans un délai maximum de 30 jours.",
        footer: `Délai de traitement : immédiat pour la prospection commerciale, 30 jours maximum sinon (art. 21 RGPD). ${CONTROLLER_LINE}`,
    },
]

export const GDPR_RIGHT_SLUGS = GDPR_RIGHTS.map((right) => right.slug) as [GdprRightSlug, ...GdprRightSlug[]]

export function isGdprRightSlug(value: string | null | undefined): value is GdprRightSlug {
    return GDPR_RIGHTS.some((right) => right.slug === value)
}

export function findGdprRight(slug: GdprRightSlug): GdprRightConfig {
    return GDPR_RIGHTS.find((right) => right.slug === slug) ?? GDPR_RIGHTS[0]
}

export const GDPR_GENERIC_TITLE = "Exercer vos droits sur vos données personnelles"
export const GDPR_GENERIC_INTRO =
    "Conformément au Règlement général sur la protection des données (RGPD), vous disposez de droits sur les données personnelles que Réassurez-moi détient à votre sujet. Sélectionnez le droit que vous souhaitez exercer pour afficher le formulaire correspondant."
export const GDPR_GENERIC_FOOTER = CONTROLLER_LINE
export const GDPR_INFO_BOX_TEXT =
    "Les informations demandées nous servent uniquement à retrouver votre dossier et à vérifier votre identité. Elles doivent correspondre à celles que vous avez utilisées lors de votre demande de devis."
export const GDPR_ERASURE_INFO_BOX_TEXT =
    "Votre adresse email nous sert uniquement à retrouver votre dossier. Elle doit correspondre à celle utilisée lors de votre demande de devis."

// The erasure form answers the same whether the address is on file or not, so nobody can probe who is a customer
export const GDPR_ERASURE_SUCCESS_TITLE = "Vérifiez votre boîte mail"
export const GDPR_ERASURE_SUCCESS_BEFORE_EMAIL = "Si l'adresse "
export const GDPR_ERASURE_SUCCESS_AFTER_EMAIL =
    " correspond à un dossier chez Réassurez-moi, vous allez recevoir un email contenant un lien de confirmation. Cliquez dessus dans les 48 heures pour confirmer votre demande : sans confirmation, elle sera automatiquement annulée."
export const GDPR_ERASURE_SUCCESS_HINT =
    "Aucune donnée n'est modifiée tant que vous n'avez pas confirmé. Si vous ne recevez rien, vérifiez vos courriers indésirables et l'orthographe de l'adresse saisie."

export const GDPR_ERASURE_LEGAL_NOTICE: readonly string[] = [
    "Réassurez-moi, responsable de traitement, recueille votre email dans le présent formulaire afin de traiter votre demande d'exercice des droits relatifs à vos données personnelles. Les informations signalées comme obligatoires sont nécessaires au traitement de votre demande. À défaut, nous ne serons pas en mesure d'y répondre ou de vérifier votre identité lorsque cela est nécessaire. La transmission d'un justificatif d'identité pourra être sollicitée uniquement en cas de doute raisonnable sur votre identité.",
    "Les données sont conservées pendant l'année de leur réception afin de traiter votre demande, puis pendant 5 ans pour gérer un éventuel contentieux. Elles sont accessibles uniquement aux personnes habilitées à traiter votre demande.",
]
export const GDPR_ERASURE_LEGAL_NOTICE_POLICY_LINE =
    "Pour en savoir plus sur la gestion de vos données et pour l'exercice de vos droits, vous pouvez consulter notre politique de protection des données personnelles :"

export interface GdprSelectOption<V extends string = string> {
    value: V
    label: string
}

export type GdprAccessScope = "all" | "specific"
export type GdprRectificationField = "nom" | "prenom" | "email" | "telephone" | "adresse"
export type GdprRestrictionReason = "accuracy" | "lawfulness" | "other"
export type GdprPortabilityFormat = "csv" | "json"
export type GdprObjectionProcessing = "marketing" | "other"

export const GDPR_ACCESS_SCOPES: readonly GdprSelectOption<GdprAccessScope>[] = [
    {value: "all", label: "Toutes mes données"},
    {value: "specific", label: "Données spécifiques"},
]

export const GDPR_RECTIFICATION_FIELDS: readonly GdprSelectOption<GdprRectificationField>[] = [
    {value: "nom", label: "Nom"},
    {value: "prenom", label: "Prénom"},
    {value: "email", label: "Email"},
    {value: "telephone", label: "Téléphone"},
    {value: "adresse", label: "Adresse"},
]

export const GDPR_RESTRICTION_REASONS: readonly GdprSelectOption<GdprRestrictionReason>[] = [
    {value: "accuracy", label: "Je conteste l'exactitude d'une donnée"},
    {value: "lawfulness", label: "Je conteste la licéité d'un traitement"},
    {value: "other", label: "Autre"},
]

export const GDPR_PORTABILITY_FORMATS: readonly GdprSelectOption<GdprPortabilityFormat>[] = [
    {value: "csv", label: "CSV"},
    {value: "json", label: "JSON"},
]

export const GDPR_OBJECTION_PROCESSINGS: readonly GdprSelectOption<GdprObjectionProcessing>[] = [
    {value: "marketing", label: "Prospection commerciale"},
    {value: "other", label: "Autre traitement"},
]
